<template>
  <slot :print="printVersion"></slot>

  <v-dialog :model-value="printing" persistent width="360">
    <v-card rounded="xl">
      <v-card-text class="pt-6 pb-6 d-flex justify-center">
        <LoadingSpinner text="Preparing printable view..." />
      </v-card-text>
    </v-card>
  </v-dialog>

  <div ref="host" class="historical-print-host" aria-hidden="true">
    <c-e-module-renderer
      v-if="showRenderer"
      :cemodule-id="moduleId"
      :page-path="pagePath"
      :apply-context="true"
      :html-content="htmlContent"
      :md-content="''"
      :tree-content="treeContent"
      :is-end-page="true"
      :use-historical-context="true"
      @raw-page-error="onRawPageError"
    ></c-e-module-renderer>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { CSBase } from '@complispace/cs-design-system';
import markdownIt from 'markdown-it';
import { markdownItFancyListPlugin } from 'markdown-it-fancy-lists';
import moment from 'moment-timezone';
import CEModuleRenderer from '@/components/content/CEModuleRenderer';
import LoadingSpinner from '@/components/core/LoadingSpinner';
import { content } from '@/api';
import { CE_MAIN_BRANCH } from '@/constants';
import * as types from '@/store/mutationTypes';
import updateTableColumnsWidth from '@/lib/tableUtils';
import { loadImageFromS3 } from '@/lib/imageRenderUtils';
import { buildHistoricalTitle, openPrintWindow } from '@/lib/historicalPrintView';

// The print path has no pageCommitOID, so the renderer emits click-to-edit context-key
// spans. A PDF must not carry their highlight or pointer cursor.
const EDIT_AFFORDANCE_CLASSES = ['validContext', 'placeholder', 'ce-cursor'];

// Mirrors Page.vue:237 — note the `.disable('code')`, which CEModuleRenderer omits.
const md = markdownIt({
  html: true
}).disable('code');

md.use(markdownItFancyListPlugin);

export default {
  name: 'HistoricalPrintRenderer',

  components: {
    'c-e-module-renderer': CEModuleRenderer,
    LoadingSpinner
  },

  extends: CSBase,

  props: {
    moduleId: { type: Number, required: true },
    pagePath: { type: String, required: true }
  },

  data() {
    return {
      htmlContent: '',
      showRenderer: false,
      treeContent: null,
      rawPageError: false,
      printing: false
    };
  },

  computed: {
    ...mapState({
      logoUri: (state) => state.client.logoUri,
      isPublicViewMode: (state) => state.appearance.isPublicViewMode
    }),

    ...mapGetters({
      previousContextItemValues: 'clientContext/previousValues'
    })
  },

  methods: {
    onRawPageError(value) {
      this.rawPageError = value;
    },

    // util.js's removeContextKeyClass/removeClickableProperty are regex-based and match
    // none of this markup, so work off the DOM instead.
    stripEditAffordances(element) {
      const clone = element.cloneNode(true);
      const selector = EDIT_AFFORDANCE_CLASSES.map((name) => `.${name}`).join(',');

      clone.querySelectorAll(selector).forEach((node) => {
        node.classList.remove(...EDIT_AFFORDANCE_CLASSES);
      });

      // Tooltip carries the literal "This value can't be edited." — drop the node.
      clone.querySelectorAll('.tooltip-text').forEach((node) => node.remove());

      return clone.innerHTML;
    },

    async printVersion({ id, date }) {
      if (this.printing) return;
      this.printing = true;

      try {
        this.setLoading(true);

        const response = await content.getPage(
          this.moduleId,
          CE_MAIN_BRANCH,
          encodeURIComponent(this.pagePath),
          false,
          id,
          date,
          this.isPublicViewMode
        );

        if (response.status !== 200) {
          throw new Error('Unable to load the selected version.');
        }

        const { page, path, children, contextKeys, assuranceDetails } = response.data;

        this.$store.commit(
          types.CLIENT_CONTEXT_BULK_ADD_PREVIOUS_CLIENT_CONTEXT_ITEMS,
          contextKeys
        );

        let renderedPage = md.render(page);

        if (renderedPage.includes('</th>') || renderedPage.includes('</td>')) {
          renderedPage = updateTableColumnsWidth(renderedPage);
        }

        this.treeContent = { path, page: renderedPage, children };

        if (Object.prototype.hasOwnProperty.call(response.data, 'assuranceDetails')) {
          this.treeContent.assuranceData = assuranceDetails;
        }

        // Mount empty first: the child's renderedContent watcher is non-immediate and
        // is what runs the link-fix pass, so it needs a real null -> html transition.
        this.showRenderer = true;
        await this.$nextTick();

        this.htmlContent = renderedPage;

        await this.$nextTick();
        // CEModuleRenderer fixes links inside setTimeout(..., 0); wait for that pass.
        await new Promise((resolve) => setTimeout(resolve, 0));

        if (this.rawPageError) {
          throw new Error('The selected version could not be displayed.');
        }

        const rendered = this.$refs.host.querySelector('.rendered-content');

        if (!rendered) {
          throw new Error('The selected version could not be displayed.');
        }

        // Inline images while off-screen, scoped to our own subtree. Parallel rather
        // than loadAllImages (serial) — the window can only open once this finishes.
        await Promise.all(
          Array.from(rendered.querySelectorAll('.client-private-image')).map((img) =>
            loadImageFromS3(img.dataset.filenameHash, img)
          )
        );

        await openPrintWindow({
          html: this.stripEditAffordances(rendered),
          title: buildHistoricalTitle(page, this.previousContextItemValues),
          logoUri: this.logoUri,
          versionDate: moment.tz(date, 'Australia/Sydney').format('DD/MM/YYYY HH:mm')
        });
      } catch (error) {
        this.showErrorResponseAlert(error);
      } finally {
        this.htmlContent = '';
        this.showRenderer = false;
        this.treeContent = null;
        this.rawPageError = false;
        this.printing = false;
        this.setLoading(false);
      }
    }
  }
};
</script>

<style scoped>
.historical-print-host {
  position: absolute;
  visibility: hidden;
}
</style>
